<script setup lang="ts">
import { createReusableTemplate } from '@vueuse/core';

import { STORYBLOK_ROUTES } from '#shared/constants/storyblok';
import {
    AuctionState,
    ComputedState,
    CustomerValueAddedServiceCode,
    type CustomerValueAddedServiceDto,
    DirectSaleComputedState,
    SellingMethod,
} from '~/apiClient';
import type { VasPowertrainWarrantyDuration } from '~/components/Vas/VasPowertrainWarrantyFormField.vue';
import { INJECTION_KEYS } from '~/constants/injectionKeys';
import { type PlatformSaleItemEntity } from '~/types/sale.type';

type ExtendedVas = CustomerValueAddedServiceDto & {
    title: string;
    tooltipText: string;
};

// PROPS
const { sale } = defineProps({
    sale: { type: Object as PropType<PlatformSaleItemEntity>, required: true },
});

// COMPOSABLES
const { tt, tn, formatUnits } = useTypedI18n();
const { isLoggedIn } = useAuthUtils();
const { openAuthModal } = useModals();
const { title: provisionalTitle, tooltipText: provisionalTooltipText } = useProvisionalLicensePlateLocale(
    () => sale.mainAsset.registrationDocumentCountry
);
const fleequidCareAndPowertrainWarrantyGuideTo = await useStoryblokTranslatedRoute({ slug: STORYBLOK_ROUTES.FLEEQUID_CARE_POWERTRAIN_WARRANTY });

const [DefineVasTitleTemplate, ReuseVasTitleTemplate] = createReusableTemplate<{ vas: ExtendedVas }, { default: undefined; tooltip: undefined }>();
const [DefineVasPriceTemplate, ReuseVasPriceTemplate] = createReusableTemplate<{ vas: ExtendedVas; price?: number }>();

// DATA
const isLogisticsModalOpen = ref(false);
const logisticsPrice = ref<number>();
const logisticsFullAddress = ref<string>();

const isPowertrainWarrantyModalOpen = ref(false);
const powertrainWarrantyPrice = ref<number>();
const powertrainWarrantyDuration = ref<VasPowertrainWarrantyDuration>();

// Info modals
const isPowertrainWarrantyInfoModalOpen = ref(false);
const isFleequidCareInfoModalOpen = ref(false);

const valueAddedServices = inject(INJECTION_KEYS.VALUE_ADDED_SERVICES);
const salesContact = inject(INJECTION_KEYS.SALES_CONTACT);

// Avoid show VAS for sold Sale (see BSD-2193 for further details)
// It can lead to ambiguity between the VAS that are available and those that have actually been purchased.
const showVas = computed(() => {
    if (sale.sellingMethod === SellingMethod.Auction) {
        return [AuctionState.Pending, AuctionState.Running].includes(sale.state) || sale.computedState === ComputedState.UnderNegotiation;
    }

    if (sale.sellingMethod === SellingMethod.DirectSale) {
        return sale.computedState === DirectSaleComputedState.NoActiveOrder;
    }

    return false;
});

const extendedVas = computed<ExtendedVas[]>(
    () =>
        sortVasList(valueAddedServices?.value)
            ?.filter(vas => {
                const optimisticGuestPurchasable = vas.isUserInformationRequired && !isLoggedIn.value;

                return vas.enabled && (vas.purchasable || optimisticGuestPurchasable);
            })
            .map(vas => {
                const valueAddedServiceCode = convertToVasCode(vas.valueAddedServiceCode);
                const isProvisionalLicensePlate = vas.valueAddedServiceCode === CustomerValueAddedServiceCode.ProvisionalLicensePlate;

                const title = isProvisionalLicensePlate && provisionalTitle.value ? provisionalTitle.value : tt(`vas.${valueAddedServiceCode}.title`);

                const tooltipText =
                    isProvisionalLicensePlate && provisionalTooltipText.value
                        ? provisionalTooltipText.value
                        : tt(`sdp.vas.tooltip.${vas.valueAddedServiceCode}`);

                return { ...vas, title, tooltipText };
            }) ?? []
);

const hasLogistics = computed(() => !!extendedVas.value.find(vas => vas.valueAddedServiceCode === CustomerValueAddedServiceCode.LogisticsService));
const hasPowertrainWarranty = computed(
    () => !!extendedVas.value.find(vas => vas.valueAddedServiceCode === CustomerValueAddedServiceCode.PowertrainWarranty)
);
const hasFleequidCare = computed(() => !!extendedVas.value.find(vas => vas.valueAddedServiceCode === CustomerValueAddedServiceCode.FleequidCare));

// METHODS
const onSelectedAddress = (price?: number, fullAddress?: string): void => {
    logisticsPrice.value = price;
    logisticsFullAddress.value = fullAddress;
};

const onSelectPowertrainWarranty = (price?: number, duration?: VasPowertrainWarrantyDuration): void => {
    powertrainWarrantyPrice.value = price;
    powertrainWarrantyDuration.value = duration;
};
</script>

<template>
    <DefineVasTitleTemplate v-slot="{ $slots, vas }">
        <span class="flex min-w-0 items-center gap-1">
            <component :is="$slots.tooltip" v-if="!!$slots.tooltip" />
            <BaseTooltip v-else :text="vas.tooltipText">
                <UIcon class="size-4 shrink-0" name="lucide:info" />
            </BaseTooltip>
            <span class="truncate text-neutral-700">
                <component :is="$slots.default" v-if="!!$slots.default" />
                <span v-else>{{ vas.title }}</span>
                <span v-if="!isLoggedIn" class="ms-0.5">*</span>
            </span>
        </span>
    </DefineVasTitleTemplate>
    <DefineVasPriceTemplate v-slot="{ $slots, vas, price }">
        <span v-if="price" class="text-primary shrink-0 font-semibold">{{ tn(price, 'currency') }}</span>
        <span v-else-if="vas.totalPrice" class="text-primary shrink-0 font-semibold">{{ tn(vas.totalPrice, 'currency') }}</span>
        <component :is="$slots.default" v-else />
    </DefineVasPriceTemplate>
    <div v-if="extendedVas.length && showVas" class="p-4 lg:p-6">
        <p class="text-sm font-semibold">{{ tt('sdp.vas.title') }}</p>

        <div v-for="vas of extendedVas" :key="vas.valueAddedServiceCode" class="text-sm leading-6">
            <template v-if="vas.valueAddedServiceCode === CustomerValueAddedServiceCode.LogisticsService">
                <div class="flex justify-between gap-2">
                    <ReuseVasTitleTemplate :vas="vas" />

                    <ReuseVasPriceTemplate :vas="vas" :price="logisticsPrice">
                        <UButton
                            variant="link"
                            size="xs"
                            :label="tt('sdp.vas.selection.compute')"
                            class="p-0 text-sm"
                            trailing-icon="lucide:square-pen"
                            @click="isLogisticsModalOpen = true"
                        />
                    </ReuseVasPriceTemplate>
                </div>

                <div v-if="logisticsFullAddress" class="flex justify-between gap-6">
                    <span class="text-primary truncate text-xs leading-6 italic">{{ logisticsFullAddress }}</span>

                    <UButton
                        variant="link"
                        size="xs"
                        :label="tt('sdp.vas.selection.update')"
                        class="p-0 text-sm"
                        trailing-icon="lucide:square-pen"
                        @click="isLogisticsModalOpen = true"
                    />
                </div>
            </template>
            <template v-else-if="vas.valueAddedServiceCode === CustomerValueAddedServiceCode.PowertrainWarranty">
                <div class="flex justify-between gap-2">
                    <ReuseVasTitleTemplate :vas="vas">
                        <template #tooltip>
                            <UIcon class="size-4 shrink-0 cursor-pointer" name="lucide:info" @click="isPowertrainWarrantyInfoModalOpen = true" />
                        </template>
                        <BaseI18nT keypath="vas.PowertrainWarranty.highlightedTitle.base" tag="span">
                            <template #highlight>
                                <UButton
                                    class="p-0"
                                    size="md"
                                    variant="link"
                                    :label="tt('vas.PowertrainWarranty.highlightedTitle.highlight')"
                                    @click="isPowertrainWarrantyInfoModalOpen = true"
                                />
                            </template>
                        </BaseI18nT>
                    </ReuseVasTitleTemplate>

                    <ReuseVasPriceTemplate :vas="vas" :price="powertrainWarrantyPrice">
                        <UButton
                            variant="link"
                            size="xs"
                            :label="tt('sdp.vas.selection.compute')"
                            class="p-0 text-sm"
                            trailing-icon="lucide:square-pen"
                            @click="isPowertrainWarrantyModalOpen = true"
                        />
                    </ReuseVasPriceTemplate>
                </div>

                <div v-if="powertrainWarrantyDuration" class="flex justify-between gap-6">
                    <span class="truncate text-xs leading-6 italic">{{
                        tt('vas.PowertrainWarranty.duration.itemLabel', {
                            months: powertrainWarrantyDuration.durationMonths,
                            km: formatUnits.km(powertrainWarrantyDuration.mileageLimitKm),
                        })
                    }}</span>

                    <UButton
                        variant="link"
                        size="xs"
                        :label="tt('sdp.vas.selection.update')"
                        class="p-0 text-sm"
                        trailing-icon="lucide:square-pen"
                        @click="isPowertrainWarrantyModalOpen = true"
                    />
                </div>
            </template>
            <template v-else>
                <div class="flex justify-between gap-2">
                    <ReuseVasTitleTemplate v-if="vas.valueAddedServiceCode === CustomerValueAddedServiceCode.FleequidCare" :vas="vas">
                        <template #tooltip>
                            <UIcon class="size-4 shrink-0 cursor-pointer" name="lucide:info" @click="isFleequidCareInfoModalOpen = true" />
                        </template>
                        <BaseI18nT keypath="vas.FleequidCare.highlightedTitle.base" tag="span">
                            <template #highlight>
                                <UButton
                                    class="p-0"
                                    size="md"
                                    variant="link"
                                    :label="tt('vas.FleequidCare.highlightedTitle.highlight')"
                                    @click="isFleequidCareInfoModalOpen = true"
                                />
                            </template>
                        </BaseI18nT>
                    </ReuseVasTitleTemplate>
                    <ReuseVasTitleTemplate v-else :vas="vas" />

                    <ReuseVasPriceTemplate :vas="vas" />
                </div>
            </template>
        </div>

        <p v-if="!isLoggedIn" class="text-xs">
            <span class="me-0.5">*</span>
            <BaseI18nT tag="span" keypath="sdp.vas.guestDisclaimer">
                <template #loginOrSignup>
                    <UButton class="p-0" size="sm" variant="link" :label="tt('sdp.vas.loginOrSignup')" @click="openAuthModal()" />
                </template>
            </BaseI18nT>
        </p>
    </div>

    <!-- VAS compute price modals -->
    <LazySdpSalePanelVasLogisticsModal v-if="hasLogistics" v-model:open="isLogisticsModalOpen" :sale="sale" @select-address="onSelectedAddress" />
    <LazySdpSalePanelVasPowertrainWarrantyModal
        v-if="hasPowertrainWarranty"
        v-model:open="isPowertrainWarrantyModalOpen"
        @select-powertrain-warranty="onSelectPowertrainWarranty"
    />

    <!-- VAS info modals -->
    <LazyVasPowertrainWarrantyInfoModal
        v-if="hasPowertrainWarranty"
        v-model:open="isPowertrainWarrantyInfoModalOpen"
        :guide-to="fleequidCareAndPowertrainWarrantyGuideTo"
        :sales-contact="salesContact"
    />
    <LazyVasFleequidCareInfoModal
        v-if="hasFleequidCare"
        v-model:open="isFleequidCareInfoModalOpen"
        :guide-to="fleequidCareAndPowertrainWarrantyGuideTo"
        :sales-contact="salesContact"
    />
</template>
